import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { DashboardLayout } from '../components/DashboardLayout';
import { Button } from '../components/ui/button';
import {
  BellRing,
  Briefcase,
  TrendingUp,
  Plus,
  ArrowRight,
  Clock,
  MapPin,
  Building2,
  AlertCircle,
} from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const DashboardPage = () => {
  const { user, token } = useAuth();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [recentJobs, setRecentJobs] = useState([]);

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const response = await axios.get(`${API_URL}/api/dashboard/stats`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setStats(response.data);
        if (response.data.recent_matches) {
          setRecentJobs(response.data.recent_matches);
        }
      } catch (error) {
        console.error('Failed to fetch stats:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchStats();
  }, [token]);

  const userName = user?.full_name ? user.full_name.split(' ')[0] : 'User';
  const activeAlerts = stats?.active_alerts || 0;
  const totalMatches = stats?.total_matches || 0;
  const unreadMatches = stats?.unread_matches || 0;
  const subscriptionActive = user?.subscription_active || false;
  const expiresDate = stats?.subscription_expires ? new Date(stats.subscription_expires).toLocaleDateString() : 'N/A';

  return (
    <DashboardLayout>
      <div className="space-y-8" data-testid="dashboard-page">
        {/* Welcome Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-slate-900">
              Welcome back, {userName}!
            </h1>
            <p className="text-slate-600 mt-1">Here's what's happening with your job alerts</p>
          </div>
          <Link to="/alerts/new">
            <Button className="bg-[#00A651] hover:bg-[#008C44] text-white rounded-full" data-testid="create-alert-btn">
              <Plus className="w-4 h-4 mr-2" />
              New Alert
            </Button>
          </Link>
        </div>

        {/* Subscription Warning */}
        {!subscriptionActive && (
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <h3 className="font-semibold text-amber-900">Subscription Required</h3>
              <p className="text-amber-700 text-sm mt-1">
                Activate your subscription to create job alerts and receive notifications.
              </p>
            </div>
            <Link to="/subscription">
              <Button size="sm" className="bg-amber-600 hover:bg-amber-700 text-white">
                Subscribe
              </Button>
            </Link>
          </div>
        )}

        {/* Stats Cards */}
        <div className="grid sm:grid-cols-3 gap-4">
          <Link to="/alerts">
            <div className="bg-white rounded-xl border border-slate-200 p-6 card-hover">
              <div className="flex items-center justify-between mb-4">
                <div className="w-12 h-12 rounded-lg bg-[#E2F5EA] text-[#00A651] flex items-center justify-center">
                  <BellRing className="w-6 h-6" />
                </div>
                <ArrowRight className="w-5 h-5 text-slate-400" />
              </div>
              <p className="text-3xl font-bold text-slate-900">{activeAlerts}</p>
              <p className="text-sm text-slate-600 mt-1">Active Alerts</p>
            </div>
          </Link>
          
          <Link to="/jobs">
            <div className="bg-white rounded-xl border border-slate-200 p-6 card-hover">
              <div className="flex items-center justify-between mb-4">
                <div className="w-12 h-12 rounded-lg bg-blue-50 text-blue-600 flex items-center justify-center">
                  <Briefcase className="w-6 h-6" />
                </div>
                <ArrowRight className="w-5 h-5 text-slate-400" />
              </div>
              <p className="text-3xl font-bold text-slate-900">{totalMatches}</p>
              <p className="text-sm text-slate-600 mt-1">Total Matches</p>
            </div>
          </Link>
          
          <Link to="/jobs">
            <div className="bg-white rounded-xl border border-slate-200 p-6 card-hover">
              <div className="flex items-center justify-between mb-4">
                <div className="w-12 h-12 rounded-lg bg-amber-50 text-amber-600 flex items-center justify-center">
                  <TrendingUp className="w-6 h-6" />
                </div>
                <ArrowRight className="w-5 h-5 text-slate-400" />
              </div>
              <p className="text-3xl font-bold text-slate-900">{unreadMatches}</p>
              <p className="text-sm text-slate-600 mt-1">Unread Matches</p>
            </div>
          </Link>
        </div>

        {/* Subscription Status */}
        {subscriptionActive && (
          <div className="bg-[#E2F5EA] rounded-xl p-6 border border-[#00A651]/20">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="font-semibold text-[#005C2D]">Subscription Active</h3>
                <p className="text-[#00A651] text-sm mt-1">Expires: {expiresDate}</p>
              </div>
              <div className="w-10 h-10 rounded-full bg-[#00A651] flex items-center justify-center">
                <TrendingUp className="w-5 h-5 text-white" />
              </div>
            </div>
          </div>
        )}

        {/* Recent Matches */}
        <div>
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-semibold text-slate-900">Recent Job Matches</h2>
            <Link to="/jobs" className="text-[#00A651] text-sm font-medium hover:underline">
              View all
            </Link>
          </div>

          {loading && (
            <div className="bg-white rounded-xl border border-slate-200 p-8 text-center">
              <div className="animate-pulse space-y-4">
                <div className="h-20 bg-slate-100 rounded-lg" />
                <div className="h-20 bg-slate-100 rounded-lg" />
              </div>
            </div>
          )}

          {!loading && recentJobs.length === 0 && (
            <div className="bg-white rounded-xl border border-slate-200 p-8 text-center">
              <div className="w-16 h-16 rounded-full bg-slate-100 flex items-center justify-center mx-auto mb-4">
                <Briefcase className="w-8 h-8 text-slate-400" />
              </div>
              <h3 className="font-semibold text-slate-900 mb-2">No job matches yet</h3>
              <p className="text-slate-600 text-sm mb-4">
                Create your first job alert to start receiving matches
              </p>
              <Link to="/alerts/new">
                <Button className="bg-[#00A651] hover:bg-[#008C44] text-white rounded-full">
                  <Plus className="w-4 h-4 mr-2" />
                  Create Alert
                </Button>
              </Link>
            </div>
          )}

          {!loading && recentJobs.length > 0 && (
            <div className="space-y-3">
              {recentJobs.map((job) => {
                const cardClass = job.is_read 
                  ? 'bg-white rounded-xl border border-slate-200 p-4 card-hover' 
                  : 'bg-[#F0FDF4] rounded-xl border border-[#00A651]/50 p-4 card-hover';
                return (
                  <div key={job.id} className={cardClass}>
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1 min-w-0">
                        <h3 className="font-semibold text-slate-900 truncate">{job.title}</h3>
                        <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-2 text-sm text-slate-600">
                          <span className="flex items-center gap-1">
                            <Building2 className="w-4 h-4" />
                            {job.employer}
                          </span>
                          <span className="flex items-center gap-1">
                            <MapPin className="w-4 h-4" />
                            {job.location}
                          </span>
                          <span className="flex items-center gap-1">
                            <Clock className="w-4 h-4" />
                            {job.grade}
                          </span>
                        </div>
                      </div>
                      <a href={job.job_url} target="_blank" rel="noopener noreferrer" className="flex-shrink-0">
                        <Button size="sm" variant="outline" className="border-[#00A651] text-[#00A651] hover:bg-[#E2F5EA]">
                          View
                        </Button>
                      </a>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </DashboardLayout>
  );
};

export default DashboardPage;
